// Helper functions for FormulaTestsModal

/**
 * Get the current formula expression for a field from the document
 */
export const getCurrentFieldExpression = (field, doc) => {
  if (!field?.formulas || field.formulas.length === 0) return '';
  
  // For now, just take the first formula expression
  const firstFormula = field.formulas[0];
  if (doc?.formulas) {
    const formulaDef = doc.formulas.find(f => f._id === firstFormula.formula);
    return formulaDef ? formulaDef.expression : '';
  }
  return '';
};

/**
 * Get the formula _id for a field
 */
export const getFieldFormulaId = (field) => {
  if (!field?.formulas || field.formulas.length === 0) return '';
  // For now, just take the first formula _id
  const firstFormula = field.formulas[0];
  return firstFormula.formula || '';
};

/**
 * Check if the formula definition referenced by a field actually exists in doc.formulas.
 * Returns false for "orphan" references where field.formulas has an entry but
 * the corresponding definition is missing from doc.formulas.
 */
export const fieldHasFormulaDefinition = (field, doc) => {
  if (!field?.formulas || field.formulas.length === 0) return false;

  const formulaRefId = field.formulas[0].formula;
  if (!formulaRefId || !doc?.formulas) return false;

  return doc.formulas.some(f => f._id === formulaRefId);
};

/**
 * Format a value for display
 * @param {any} value - The value to format
 * @param {object} field - Optional field object containing type and options for dropdown/multiselect resolution
 * @param {object} originalField - Optional original field containing options if not present in field
 */
export const formatValue = (value, field, originalField = null) => {
  if (value === null || value === undefined) return 'null';
  
  // Use the field that has options (either the passed field or the original field)
  const fieldWithOptions = (field && field.options) ? field : originalField;
  
  // Handle dropdown and multiselect fields - resolve IDs to actual values
  if (fieldWithOptions && (fieldWithOptions.type === 'dropdown' || fieldWithOptions.type === 'multiselect' || fieldWithOptions.type === 'multiSelect') && fieldWithOptions.options) {
    // Create a lookup map for options
    const optionsMap = {};
    fieldWithOptions.options.forEach(option => {
      if (!option.deleted) {
        optionsMap[option._id] = option.value;
      }
    });
    
    // Handle multiselect (array of IDs)
    if (Array.isArray(value)) {
      const resolvedValues = value.map(id => {
        const optionValue = optionsMap[id];
        return optionValue !== undefined ? optionValue : id; // Fallback to ID if not found
      });
      return `[${resolvedValues.map(v => typeof v === 'string' ? `"${v}"` : v).join(', ')}]`;
    }
    
    // Handle single dropdown value
    if (typeof value === 'string' && optionsMap[value] !== undefined) {
      const resolvedValue = optionsMap[value];
      return typeof resolvedValue === 'string' ? `"${resolvedValue}"` : String(resolvedValue);
    }
  }
  
  // Default formatting for other types
  if (typeof value === 'string') return `"${value}"`;
  if (Array.isArray(value)) return `[${value.join(', ')}]`;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/**
 * Group fields by page
 */
export const groupFieldsByPage = (doc) => {
  const fieldMap = new Map();
  const pageMap = new Map();
  
  // Build field map - include ALL fields, not just those with formulas
  if (doc.fields) {
    doc.fields.forEach(field => {
      fieldMap.set(field._id, field);
    });
  }
  
  // Build page map and group fields
  if (doc.files && doc.files[0] && doc.files[0].pages) {
    doc.files[0].pages.forEach(page => {
      pageMap.set(page._id, {
        ...page,
        fields: []
      });
    });
    
    // Add fields to their respective pages based on fieldPositions
    doc.files[0].pages.forEach(page => {
      if (page.fieldPositions) {
        page.fieldPositions.forEach(position => {
          const field = fieldMap.get(position.field);
          if (field) {
            pageMap.get(page._id).fields.push({
              ...field,
              position,
              hasFormulas: field.formulas && field.formulas.length > 0
            });
          }
        });
      }
    });
  }
  
  return Array.from(pageMap.values()).filter(page => page.fields.length > 0);
};

/**
 * Get general warnings that are not field-specific
 */
export const getGeneralWarnings = (resolvedResult) => {
  if (!resolvedResult?.warnings) return [];
  
  return resolvedResult.warnings.filter(warning => {
    // Include warnings that don't have a fieldId or have general error types
    if (!warning.fieldId) return true;
    
    // Include formula evaluation errors and other high-level issues
    if (warning.type === 'FORMULA_EVAL_ERROR' || 
        warning.type === 'CIRCULAR_DEPENDENCY' ||
        warning.type === 'MISSING_DEPENDENCY' ||
        warning.message?.includes('evaluation failed') ||
        warning.message?.includes('not defined')) {
      return true;
    }
    
    return false;
  });
};

/**
 * Get warnings specific to a field
 */
export const getFieldWarnings = (fieldId, resolvedResult) => {
  if (!resolvedResult?.warnings) return [];
  const generalWarnings = getGeneralWarnings(resolvedResult);
  const generalWarningMessages = new Set(generalWarnings.map(w => w.message));
  
  return resolvedResult.warnings.filter(warning => {
    // Only include warnings that are specific to this field AND not already shown as general warnings
    const isFieldSpecific = warning.fieldId === fieldId || warning.message?.includes(fieldId);
    const isNotGeneralWarning = !generalWarningMessages.has(warning.message);
    
    return isFieldSpecific && isNotGeneralWarning;
  });
};

/**
 * Get field changes between before and after documents
 */
export const getFieldChanges = (fieldId, beforeDoc, resolvedResult) => {
  if (!resolvedResult || !beforeDoc) return null;
  
  const beforeField = beforeDoc.fields?.find(f => f._id === fieldId);
  const afterField = resolvedResult.doc?.fields?.find(f => f._id === fieldId);
  
  if (!beforeField || !afterField) return null;
  
  const beforeValue = beforeField.value;
  const afterValue = afterField.value;
  const hasChanged = JSON.stringify(beforeValue) !== JSON.stringify(afterValue);
  
  return hasChanged ? { beforeValue, afterValue } : null;
};